import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './CloudinaryUnusedImagesModal.css';
import { cloudinaryAdminService } from '../../services/api';
import { normalizeCloudinaryUnusedImagesResponse } from '../../utils/cloudinaryUtils';
import ConfirmationModal from '../../components/ConfirmationModal';

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

const CloudinaryUnusedImagesModal = ({ isOpen, onClose, onDeleted }) => {
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [stats, setStats] = useState({ totalScanned: 0, totalUnused: 0 });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const resetState = useCallback(() => {
    setError(null);
    setInfo(null);
    setImages([]);
    setSelectedIds(new Set());
    setStats({ totalScanned: 0, totalUnused: 0 });
    setConfirmDeleteOpen(false);
  }, []);

  const runScan = useCallback(async () => {
    setScanning(true);
    setError(null);
    setInfo(null);
    try {
      const res = await cloudinaryAdminService.scanUnusedImages();
      if (res?.success === false) {
        throw new Error(res?.message || res?.error || 'Cloudinary scan failed');
      }
      const parsed = normalizeCloudinaryUnusedImagesResponse(res);
      setImages(parsed.images);
      setStats({
        totalScanned: parsed.totalScanned,
        totalUnused: parsed.totalUnused,
      });
      setSelectedIds(new Set());
      if (parsed.images.length === 0) {
        setInfo('No unused Cloudinary images found. All uploaded assets appear to be in use.');
      }
    } catch (err) {
      const msg = err?.message || 'Could not scan Cloudinary images.';
      if (/404|not found|route/i.test(msg)) {
        setError(
          'Cloudinary unused-images API is not available yet. Deploy the backend route documented in docs/BACKEND_PROMPT_CLOUDINARY_UNUSED_IMAGES.md.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setScanning(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetState();
      runScan();
    }
  }, [isOpen, resetState, runScan]);

  const allSelected = images.length > 0 && selectedIds.size === images.length;
  const selectedCount = selectedIds.size;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(images.map((img) => img.publicId)));
  };

  const toggleSelect = (publicId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  };

  const selectedPublicIds = useMemo(
    () => images.filter((img) => selectedIds.has(img.publicId)).map((img) => img.publicId),
    [images, selectedIds]
  );

  const handleDeleteSelected = async () => {
    if (selectedPublicIds.length === 0) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await cloudinaryAdminService.deleteUnusedImages(selectedPublicIds);
      if (res?.success === false) {
        throw new Error(res?.message || res?.error || 'Delete failed');
      }
      const deletedSet = new Set(selectedPublicIds);
      setImages((prev) => prev.filter((img) => !deletedSet.has(img.publicId)));
      setSelectedIds(new Set());
      setStats((prev) => ({
        ...prev,
        totalUnused: Math.max(0, prev.totalUnused - selectedPublicIds.length),
      }));
      setInfo(`${selectedPublicIds.length} unused image(s) deleted from Cloudinary.`);
      if (typeof onDeleted === 'function') onDeleted(selectedPublicIds.length);
    } catch (err) {
      setError(err?.message || 'Could not delete selected images.');
    } finally {
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="cloudinary-unused-modal-overlay"
        onClick={() => {
          if (!confirmDeleteOpen) onClose();
        }}
        role="presentation"
      >
        <div
          className="cloudinary-unused-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cloudinary-unused-title"
        >
          <div className="cloudinary-unused-modal__header">
            <div>
              <h2 id="cloudinary-unused-title">
                <i className="fas fa-cloud" aria-hidden />
                Cloudinary unused images
              </h2>
              <p>Images stored on Cloudinary that are not linked in your database.</p>
            </div>
            <button type="button" className="cloudinary-unused-modal__close" onClick={onClose} aria-label="Close">
              <i className="fas fa-times" />
            </button>
          </div>

          <div className="cloudinary-unused-modal__toolbar">
            <button
              type="button"
              className="cloudinary-unused-btn cloudinary-unused-btn--secondary"
              onClick={runScan}
              disabled={scanning || deleting}
            >
              {scanning ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                  Scanning…
                </>
              ) : (
                <>
                  <i className="fas fa-search" aria-hidden />
                  Re-scan
                </>
              )}
            </button>
            {images.length > 0 && (
              <>
                <button
                  type="button"
                  className="cloudinary-unused-btn cloudinary-unused-btn--ghost"
                  onClick={toggleSelectAll}
                  disabled={scanning || deleting}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
                <button
                  type="button"
                  className="cloudinary-unused-btn cloudinary-unused-btn--danger"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={scanning || deleting || selectedCount === 0}
                >
                  {deleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" aria-hidden />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-alt" aria-hidden />
                      Delete selected ({selectedCount})
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {(stats.totalScanned > 0 || images.length > 0) && (
            <div className="cloudinary-unused-modal__stats">
              <span>Scanned: {stats.totalScanned}</span>
              <span>Unused: {stats.totalUnused}</span>
              <span>Showing: {images.length}</span>
            </div>
          )}

          {error && (
            <div className="cloudinary-unused-modal__alert cloudinary-unused-modal__alert--error">
              <i className="fas fa-exclamation-circle" aria-hidden />
              {error}
            </div>
          )}
          {info && !error && (
            <div className="cloudinary-unused-modal__alert cloudinary-unused-modal__alert--info">
              <i className="fas fa-info-circle" aria-hidden />
              {info}
            </div>
          )}

          <div className="cloudinary-unused-modal__body">
            {scanning && images.length === 0 ? (
              <div className="cloudinary-unused-modal__loading">
                <i className="fas fa-spinner fa-spin" aria-hidden />
                <p>Checking Cloudinary for unused images…</p>
              </div>
            ) : images.length === 0 && !scanning && !error ? (
              <div className="cloudinary-unused-modal__empty">
                <i className="fas fa-check-circle" aria-hidden />
                <p>No unused images to show.</p>
              </div>
            ) : (
              <div className="cloudinary-unused-grid">
                {images.map((img) => {
                  const checked = selectedIds.has(img.publicId);
                  return (
                    <label
                      key={img.publicId}
                      className={`cloudinary-unused-card${checked ? ' is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(img.publicId)}
                        disabled={deleting}
                      />
                      <div className="cloudinary-unused-card__thumb">
                        <img src={img.url} alt={img.publicId} loading="lazy" />
                      </div>
                      <div className="cloudinary-unused-card__meta">
                        <span className="cloudinary-unused-card__id" title={img.publicId}>
                          {img.publicId}
                        </span>
                        <span className="cloudinary-unused-card__size">
                          {img.format ? `${img.format.toUpperCase()} · ` : ''}
                          {formatBytes(img.bytes)}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteSelected}
        title="Delete unused Cloudinary images?"
        message={`Permanently delete ${selectedCount} selected image(s) from Cloudinary? This cannot be undone.`}
        overlayClassName="confirmation-overlay--stacked"
      />
    </>,
    document.body
  );
};

export default CloudinaryUnusedImagesModal;
